// src/pages/Seller/Verification.jsx
import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, Box, Button, Grid, TextField,
  FormControl, InputLabel, Select, MenuItem, Stepper, Step, StepLabel,
  Alert, CircularProgress, Chip, Divider, LinearProgress,
} from '@mui/material';
import {
  VerifiedUser, CloudUpload, CheckCircle, HourglassEmpty,
  Business, Description, ArrowForward, ArrowBack,
} from '@mui/icons-material';
import { verificationAPI } from '../../services/api';
import toast from 'react-hot-toast';

const STEPS = ['Business Details', 'Upload Documents', 'Submit'];

// ── Helper: get current user from localStorage ────────────────────────────
const getStoredUser = () => {
  try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
};

const SellerVerification = () => {
  const storedUser  = getStoredUser();
  const userCountry = storedUser?.country || 'India';
  const isGoogle    = storedUser?.oauth_provider === 'google';

  // ── Auto-fill from Google / user profile ──────────────────────────────────
  const [step,      setStep]      = useState(0);
  const [form,      setForm]      = useState({
    country:             ['India','UAE'].includes(userCountry) ? userCountry : 'India',
    business_name:       storedUser?.company_name || '',   // from Google display name
    registration_number: '',
    gst_number:          '',
    pan_number:          '',
    iec_number:          '',
    trade_license:       '',
    address:             storedUser?.address || '',
  });
  const [docFile,   setDocFile]   = useState(null);
  const [status,    setStatus]    = useState(null);   // existing verification status
  const [loading,   setLoading]   = useState(true);
  const [submitting,setSubmitting]= useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => { fetchStatus(); }, []);

  const fetchStatus = async () => {
    try {
      const res = await verificationAPI.getStatus();
      const data = res.data;
      if (data && (data.status || data.verified !== undefined)) {
        setStatus(data);
      }
    } catch {
      // no existing verification — show form
    } finally {
      setLoading(false);
    }
  };

  const fc = (field) => (e) => setForm(p => ({ ...p, [field]: e.target.value }));

  const handleSubmit = async () => {
    if (!docFile) { toast.error('Please upload at least one business document'); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
      fd.append('document', docFile);
      fd.append('user_type', 'seller');
      await verificationAPI.submit(fd);
      setSubmitted(true);
      toast.success('KYC documents submitted! Admin will review within 2 business days.');
    } catch (err) {
      console.error('KYC submit error', err);
      toast.error(err?.data?.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <Container sx={{ py: 6, textAlign: 'center' }}>
      <CircularProgress />
      <Typography sx={{ mt: 2 }} color="text.secondary">Loading verification status...</Typography>
    </Container>
  );

  // Already verified
  if (status?.verified || status?.status === 'verified') return (
    <Container maxWidth="sm" sx={{ py: 6, textAlign: 'center' }}>
      <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
      <Typography variant="h5" fontWeight="bold" gutterBottom>KYC Verified</Typography>
      <Typography color="text.secondary">
        Your business documents have been verified. You are a trusted seller on this platform.
      </Typography>
      <Chip label="VERIFIED" color="success" sx={{ mt: 2, fontWeight: 'bold', px: 2 }} />
    </Container>
  );

  // Rejected — allow resubmission
  if (!submitted && status?.status === 'rejected') return (
    <Container maxWidth="sm" sx={{ py: 6, textAlign: 'center' }}>
      <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
        <strong>KYC Rejected</strong><br />
        {status?.rejection_reason || 'Your documents were not accepted. Please upload correct documents and resubmit.'}
      </Alert>
      <Button variant="contained" color="primary" size="large"
        onClick={() => setStatus(null)}>
        Resubmit KYC Documents
      </Button>
    </Container>
  );

  // Pending review
  if (!submitted && status?.status === 'pending') return (
    <Container maxWidth="sm" sx={{ py: 6, textAlign: 'center' }}>
      <HourglassEmpty sx={{ fontSize: 80, color: 'warning.main', mb: 2 }} />
      <Typography variant="h5" fontWeight="bold" gutterBottom>KYC Under Review</Typography>
      <Typography color="text.secondary" paragraph>
        Your documents are being reviewed by our team. This typically takes 2 business days.
        You will be notified once the review is complete.
      </Typography>
      <Chip label="PENDING REVIEW" color="warning" sx={{ fontWeight: 'bold', px: 2, mb: 3 }} />
      <Box>
        <Button variant="outlined" size="small" color="warning" onClick={() => setStatus(null)}>
          Update / Resubmit Documents
        </Button>
      </Box>
    </Container>
  );

  // Submitted this session
  if (submitted) return (
    <Container maxWidth="sm" sx={{ py: 6, textAlign: 'center' }}>
      <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
      <Typography variant="h5" fontWeight="bold" gutterBottom>Documents Submitted!</Typography>
      <Alert severity="success" sx={{ textAlign: 'left', mb: 3 }}>
        <strong>What happens next:</strong><br />
        • Admin reviews your business documents<br />
        • You will receive an email once verified<br />
        • After verification, you can list products and receive orders
      </Alert>
      <LinearProgress color="warning" sx={{ borderRadius: 2 }} />
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Awaiting admin review
      </Typography>
    </Container>
  );

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Business KYC Verification
        </Typography>
        <Typography color="text.secondary">
          Submit your business documents to become a verified seller and start receiving orders.
        </Typography>
      </Box>

      {/* Google profile auto-fill notice */}
      {isGoogle && storedUser?.company_name && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Google profile detected.</strong> Business name pre-filled as <strong>{storedUser.company_name}</strong>.
            Please update any fields that differ from your registered business details.
          </Typography>
        </Alert>
      )}

      {/* Rejected — re-submit allowed */}
      {status?.status === 'rejected' && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Your previous submission was rejected. Please upload updated documents.
          {status.reason && <><br /><strong>Reason:</strong> {status.reason}</>}
        </Alert>
      )}

      <Stepper activeStep={step} sx={{ mb: 4 }}>
        {STEPS.map(label => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>

      <Paper sx={{ p: 4, borderRadius: 2 }}>
        {/* Step 0 — Business Details */}
        {step === 0 && (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Business /> Business Information
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Country *</InputLabel>
                  <Select label="Country *" value={form.country} onChange={fc('country')}>
                    <MenuItem value="India">India</MenuItem>
                    <MenuItem value="UAE">UAE</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" required
                  label="Registered Business Name *"
                  value={form.business_name} onChange={fc('business_name')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small"
                  label="Business Registration Number"
                  value={form.registration_number} onChange={fc('registration_number')} />
              </Grid>

              {form.country === 'India' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="GST Number"
                      value={form.gst_number} onChange={fc('gst_number')}
                      placeholder="e.g. 22AAAAA0000A1Z5" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="PAN Number"
                      value={form.pan_number} onChange={fc('pan_number')}
                      placeholder="e.g. AAAAA0000A" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="IEC Code (Import Export Code)"
                      value={form.iec_number} onChange={fc('iec_number')}
                      placeholder="10-digit IEC" />
                  </Grid>
                </>
              )}

              {form.country === 'UAE' && (
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Trade License Number"
                    value={form.trade_license} onChange={fc('trade_license')} />
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Business Address" multiline rows={2}
                  value={form.address} onChange={fc('address')} />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button variant="contained" endIcon={<ArrowForward />}
                onClick={() => {
                  if (!form.business_name) { toast.error('Please enter your business name'); return; }
                  setStep(1);
                }}>
                Next: Upload Documents
              </Button>
            </Box>
          </Box>
        )}

        {/* Step 1 — Document Upload */}
        {step === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Description /> Upload Business Document
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Alert severity="info" sx={{ mb: 3 }}>
              Upload one primary business document (GST certificate, trade license, business registration, or similar).
              Accepted formats: PDF, JPG, PNG. Max size: 10 MB.
            </Alert>

            <Box sx={{
              border: '2px dashed',
              borderColor: docFile ? 'success.main' : 'primary.light',
              borderRadius: 2, p: 4, textAlign: 'center', cursor: 'pointer',
              bgcolor: docFile ? 'success.50' : 'background.default',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
            }}>
              <input accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }}
                id="kyc-doc-upload" type="file"
                onChange={e => setDocFile(e.target.files[0] || null)} />
              <label htmlFor="kyc-doc-upload" style={{ cursor: 'pointer', display: 'block' }}>
                {docFile ? (
                  <>
                    <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                    <Typography variant="subtitle1" color="success.main" fontWeight="bold">
                      {docFile.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {(docFile.size / 1024).toFixed(1)} KB — click to change
                    </Typography>
                  </>
                ) : (
                  <>
                    <CloudUpload sx={{ fontSize: 48, color: 'primary.light', mb: 1 }} />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Click to upload document
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      PDF, JPG or PNG — max 10 MB
                    </Typography>
                  </>
                )}
              </label>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button startIcon={<ArrowBack />} onClick={() => setStep(0)}>Back</Button>
              <Button variant="contained" endIcon={<ArrowForward />}
                onClick={() => {
                  if (!docFile) { toast.error('Please upload a document'); return; }
                  setStep(2);
                }}>
                Next: Review & Submit
              </Button>
            </Box>
          </Box>
        )}

        {/* Step 2 — Review & Submit */}
        {step === 2 && (
          <Box>
            <Typography variant="h6" gutterBottom>Review & Submit</Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={1} sx={{ mb: 3 }}>
              {[
                ['Country',              form.country],
                ['Business Name',        form.business_name],
                ['Reg. Number',          form.registration_number],
                ['GST Number',           form.gst_number],
                ['PAN Number',           form.pan_number],
                ['IEC Code',             form.iec_number],
                ['Trade License',        form.trade_license],
                ['Business Address',     form.address],
                ['Document',             docFile?.name],
              ].filter(([, v]) => v).map(([label, value]) => (
                <Grid item xs={12} sm={6} key={label}>
                  <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                    <Typography variant="body2" fontWeight="medium">{value}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Alert severity="warning" sx={{ mb: 3 }}>
              Please verify all details above before submitting. Incorrect information may delay verification.
            </Alert>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button startIcon={<ArrowBack />} onClick={() => setStep(1)}>Back</Button>
              <Button variant="contained" color="success" size="large"
                startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <VerifiedUser />}
                onClick={handleSubmit} disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit KYC Documents'}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default SellerVerification;
